import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { FileText, X, Eye, Bell, Trash2 } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QuotationNotifications = ({ isAdmin = false }) => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [dismissingAll, setDismissingAll] = useState(false);

  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const endpoint = isAdmin 
        ? `${API}/quotations/notifications/admin`
        : `${API}/quotations/notifications/customer`;
      
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNotifications(response.data.notifications || []);
    } catch (error) {
      console.error('Error fetching quotation notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [token, isAdmin]);

  useEffect(() => {
    fetchNotifications();
    
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const dismissNotification = async (notificationId) => {
    try {
      const endpoint = isAdmin 
        ? `${API}/quotations/notifications/admin/${notificationId}/dismiss`
        : `${API}/quotations/notifications/customer/${notificationId}/dismiss`;
      
      await axios.post(endpoint, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (error) {
      console.error('Error dismissing notification:', error);
      toast.error('Failed to dismiss notification');
    }
  };

  const dismissAllNotifications = async () => {
    setDismissingAll(true);
    try {
      const endpoint = isAdmin 
        ? `${API}/quotations/notifications/admin/dismiss-all`
        : `${API}/quotations/notifications/customer/dismiss-all`;
      
      await axios.post(endpoint, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNotifications([]);
      toast.success('All notifications cleared');
    } catch (error) {
      console.error('Error dismissing all notifications:', error);
      toast.error('Failed to clear notifications');
    } finally {
      setDismissingAll(false);
    }
  };

  const handleView = (notification) => {
    if (isAdmin) {
      // Navigate to quotations tab or show the quotation
      // This depends on your routing setup
      navigate('/admin?tab=quotations');
    } else {
      navigate('/my-quotations');
    }
    dismissNotification(notification.id);
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'new_quotation':
        return <FileText className="w-4 h-4 text-blue-500" />;
      case 'quotation_updated':
        return <FileText className="w-4 h-4 text-amber-500" />;
      case 'quotation_converted':
        return <FileText className="w-4 h-4 text-green-500" />;
      default:
        return <Bell className="w-4 h-4 text-gray-500" />;
    }
  };

  if (notifications.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-sm" data-testid="quotation-notifications">
      {minimized ? (
        <Button
          onClick={() => setMinimized(false)}
          className="bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg"
          data-testid="expand-quotation-notifications"
        >
          <FileText className="w-4 h-4 mr-2" />
          <Badge className="bg-white text-blue-600 ml-1">{notifications.length}</Badge>
        </Button>
      ) : (
        <Card className="shadow-xl border-blue-200 max-h-[400px] overflow-hidden">
          <div className="bg-blue-600 text-white p-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              <span className="font-medium">Quotation Updates</span>
              <Badge className="bg-white text-blue-600">{notifications.length}</Badge>
            </div>
            <div className="flex items-center gap-1">
              {/* Clear All Button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={dismissAllNotifications}
                disabled={dismissingAll}
                className="text-white hover:bg-blue-700 h-8 px-2 text-xs"
                title="Clear all notifications"
                data-testid="clear-all-notifications"
              >
                <Trash2 className="w-3 h-3 mr-1" />
                {dismissingAll ? 'Clearing...' : 'Clear All'}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setMinimized(true)}
                className="text-white hover:bg-blue-700 h-8 w-8 p-0"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          <div className="max-h-[300px] overflow-y-auto">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                className="p-3 border-b hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start gap-3">
                  <div className="mt-1">
                    {getNotificationIcon(notification.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-gray-900">{notification.title}</p>
                    <p className="text-xs text-gray-500 mt-1 line-clamp-2">{notification.message}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => handleView(notification)}
                        data-testid={`view-notification-${notification.id}`}
                      >
                        <Eye className="w-3 h-3 mr-1" /> View
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs text-gray-500 hover:text-red-500 hover:bg-red-50"
                        onClick={() => dismissNotification(notification.id)}
                        data-testid={`dismiss-notification-${notification.id}`}
                      >
                        <X className="w-3 h-3 mr-1" /> Dismiss
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

export default QuotationNotifications;
